import {
  ApplicationFormSettings,
  EventItem,
  FieldConfig, FieldConfigOption,
  FieldConfigType,
  Language,
  League,
  Race,
  RaceSettings,
  RegistrationDisplayFlags,
  SportType,
  TeamType,
  TimingSystem,
  VisibilityTypes
} from "@model";
import {environment} from "@environment";

export class ObjectValidation<T> {
  isValid?: boolean;
  errors?: { field: keyof T; errorList: string[] }[];
}

export class AllowedFieldValueMap<T> {
  constructor(public fieldKey: (keyof T), public allowedValues: any[]) {

  }
}

function validateObject<T>(obj: T, requiredFields: (keyof T)[], allowedValuesMap?: AllowedFieldValueMap<T>[], deepCheck?: boolean): ObjectValidation<T> {
  const result = new ObjectValidation<T>();
  result.isValid = true;
  result.errors = [];
  for (const field of requiredFields) {
    if (obj[field] === null || obj[field] === undefined) {
      result.isValid = false;
      if (!deepCheck) {
        return result;
      }
      result.errors.push({
        field: field,
        errorList: [`Field is missing or has a null/undefined value.`],
      });
    }
  }
  if (allowedValuesMap) {
    for (const map of allowedValuesMap) {
      let isValid: boolean = false;
      for (const allowedValue of map.allowedValues) {
        // @ts-ignore
        if (Array.isArray(obj[map.fieldKey]) && Array.isArray(allowedValue) && obj[map.fieldKey].length === 0 && allowedValue.length === 0) {
          isValid = true;
          break;
        } else if (obj[map.fieldKey] === allowedValue) {
          isValid = true;
          break;
        }
      }
      if (!isValid) {
        result.isValid = false;
        if (!deepCheck) {
          return result;
        }
        result.errors.push({
          field: map.fieldKey,
          errorList: [`Field has an invalid value. Allowed values are: ${map.allowedValues.map((val, i) => `${val}${(i < map.allowedValues.length - 1) ? ", " : ""}`)}`],
        });
      }
    }
  }
  return result;
}

function getMultiLanguageRequiredFields<T>(languageList: number[], hrKey: (keyof T), enKey: (keyof T), itKey: (keyof T), sloKey: (keyof T), srKey: (keyof T)): (keyof T)[] {
  let requiredMultiLangFields: (keyof T)[] = [];
  languageList.forEach((langId: number) => {
    switch (langId) {
      case Language.HR:
        requiredMultiLangFields.push(hrKey)
        break;
      case Language.EN:
        requiredMultiLangFields.push(enKey)
        break;
      case Language.IT:
        requiredMultiLangFields.push(itKey)
        break;
      case Language.SLO:
        requiredMultiLangFields.push(sloKey)
        break;
      case Language.SR:
        requiredMultiLangFields.push(srKey)
        break;
      default:
        break;
    }
  })
  return requiredMultiLangFields;
}


export function isEventBasicDataValid(event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<EventItem> {
  if (event) {
    let requiredFields: (keyof EventItem)[] = ["id",
      "organiser", "organiserId", "defaultLanguage",
      "status", "eventType", "start", "end",
      "city", "country", "sportOne"];
    if (environment.eventImageRequired) {
      requiredFields.push("imageIcon", "imageMedium");
    }
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<EventItem>(langList, "name", "nameEng", "nameIta", "nameSlo", "nameSr").forEach((k: (keyof EventItem)) => {
      requiredFields.push(k);
    })
    return validateObject<EventItem>(event, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceBasicDataValid(race: Race | undefined, event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<Race> {
  if (!race || !event || event.id !== race.eventId) {
    return {isValid: false, errors: []};
  }
  if (event && race && event.id === race.eventId) {
    let requiredFields: (keyof Race)[] = [
      "id", "organiser", "organiserId",
      "eventId", "status", "registrationStart",
      "registrationEnd", "raceStart", "raceEnd", "startEvery", "maxParticipants", "sport", "registrationType"];
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<Race>(langList, "name", "nameEng", "nameIta", "nameSlo", "nameSr").forEach((k: (keyof Race)) => {
      requiredFields.push(k);
    })
    switch (race.sport) {
      case SportType.ROAD_RUNNING:
      case SportType.CROSS_RUNNING:
      case SportType.CYCLING_CYCLOCROSS:
      case SportType.CYCLING_ENDURO:
      case SportType.CYCLING_GRAVEL:
      case SportType.CYCLING_MOUNTAINBIKE:
      case SportType.CYCLING_PUMPTRACK:
      case SportType.CYCLING_ROAD:
        requiredFields.push("length", "ascent", "descent", "maxAltitude", "minAltitude")
        break;
      case SportType.SWIMMING:
        requiredFields.push("length");
        break;
      default:
        break;

    }
    return validateObject<Race>(race, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceAdditionalDataValid(race: Race | undefined, event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<Race> {
  if (!race || !event || event.id !== race.eventId) {
    return {isValid: false, errors: []};
  }
  if (event && race && event.id === race.eventId) {
    let requiredFields: (keyof Race)[] = [];
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<Race>(langList, "shortDescription", "shortDescriptionEng", "shortDescriptionIta", "shortDescriptionSlo", "shortDescriptionSr").forEach((k: (keyof Race)) => {
      requiredFields.push(k);
    })
    return validateObject<Race>(race, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isLeagueBasicDataValid(league: League | undefined, deepCheck?: boolean): ObjectValidation<League> {
  if (league) {
    let requiredFields: (keyof League)[] = ["id",
      "organiser", "defaultLanguage",
      "status", "start", "end", "imageIcon", "imageMedium"];
    let langList = league.extraLanguages ? [...league.extraLanguages] : [];

    if (league?.defaultLanguage) {
      langList.push(league.defaultLanguage);
    }
    getMultiLanguageRequiredFields<League>(langList, "name", "nameEng", "nameIta", "nameSlo", "nameSr").forEach((k: (keyof League)) => {
      requiredFields.push(k);
    })
    return validateObject<League>(league, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceSettingsValid(settings: RaceSettings | undefined, race: Race | undefined, event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<RaceSettings> {
  if (settings) {
    let requiredFields: (keyof RaceSettings)[] = [
      "applicationProcedure", "category", "typeOfApplication", "measurementType", "measurementPrecisionType",
      "startByType", "resultsPerPage", "topRanked", "teamRankingGenderSeparation", "controlPoints",
      "penaltyMinutes", "minimumLapDuration", "rotatingOrder", "rotatingResultsDisplay", "rotatingResultsSeconds", "teamType", "timingSystem"
    ];
    let allowedValuesMap: AllowedFieldValueMap<RaceSettings>[] = [];
    if (settings.teamType) {
      if (settings.teamType === TeamType.SINGLE) {
        allowedValuesMap.push(new AllowedFieldValueMap("selectionOfTeamMembersType", [null]));
        allowedValuesMap.push(new AllowedFieldValueMap("teamNameRequired", [null]));
        allowedValuesMap.push(new AllowedFieldValueMap("genderRequired", [null]));
      } else {
        requiredFields.push("selectionOfTeamMembersType");
        requiredFields.push("teamNameRequired");
        requiredFields.push("genderRequired");
      }
    }
    if (!event?.paymentSettings?.mandatoryPayment) {
      allowedValuesMap.push(new AllowedFieldValueMap<RaceSettings>("enableGroupRegistrationPayment", [null, false, undefined]))
    }

    if (settings.timingSystem) {
      if (settings.timingSystem === TimingSystem.PLATFORM) {
        allowedValuesMap.push(new AllowedFieldValueMap("timingEquipment", [null]));
      } else {
        requiredFields.push("timingEquipment")
      }
    }
    if (settings.useGroupRanking) {
      requiredFields.push("groupRankingSeparation");
      requiredFields.push("teamRankingTeamSeparation");
    } else {
      allowedValuesMap.push(new AllowedFieldValueMap("rankingGroups", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("participantsFromRankingGroup", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("groupRankingSeparation", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("teamRankingTeamSeparation", [null]));
    }

    if (settings.enableGroupRegistrationPayment) {
      requiredFields.push("maxGroupRegisteredParticipants");
    }
    return validateObject<RaceSettings>(settings, requiredFields, allowedValuesMap, deepCheck);
  }
  return {isValid: false, errors: []}
}

export function isApplicationFormSettingsValid(applicationFormSettings: ApplicationFormSettings | undefined,
                                               settings: RaceSettings | undefined, event?: EventItem, deepCheck?: boolean)
  : ObjectValidation<ApplicationFormSettings> {
  if (applicationFormSettings) {
    const result = new ObjectValidation<ApplicationFormSettings>();
    result.isValid = true;
    result.errors = [];
    let allowedValuesMap: AllowedFieldValueMap<ApplicationFormSettings>[] = [];
    let requiredFields: (keyof ApplicationFormSettings)[] = []

    if (settings) {
      const teamType: number | undefined = settings.teamType;
      const genderRequired: boolean = !!settings.genderRequired;
      const selectionOfTeamMembersType: number | undefined = settings.selectionOfTeamMembersType ?? RegistrationDisplayFlags.NOT_USED;

      if (teamType) {
        if (teamType === TeamType.SINGLE || genderRequired) {
          allowedValuesMap.push(new AllowedFieldValueMap("genderVisibility", [VisibilityTypes.ALWAYS_VISIBLE]));
          allowedValuesMap.push(new AllowedFieldValueMap("genderRegistrationDisplayFlag", [RegistrationDisplayFlags.ALWAYS_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("genderRequired", [true]));
        }
        if (teamType === TeamType.SINGLE || selectionOfTeamMembersType === RegistrationDisplayFlags.NOT_USED) {
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineVisibility", [VisibilityTypes.NOT_VISIBLE]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRegistrationDisplayFlag", [RegistrationDisplayFlags.NOT_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRequired", [false]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplines", [[], null, undefined]));
        } else if (selectionOfTeamMembersType === RegistrationDisplayFlags.ALWAYS_USED) {
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRegistrationDisplayFlag", [RegistrationDisplayFlags.ALWAYS_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRequired", [true]));
          requiredFields.push("disciplines")
        }
      }
    }

    if (!!applicationFormSettings.tshirtRegistrationDisplayFlag && !(applicationFormSettings.tshirtRegistrationDisplayFlag === RegistrationDisplayFlags.NOT_USED)) {
      requiredFields.push("tshirts");
    }

    if (!!applicationFormSettings.mealRegistrationDisplayFlag && !(applicationFormSettings.mealRegistrationDisplayFlag === RegistrationDisplayFlags.NOT_USED)) {
      requiredFields.push("meals");
    }
    for (const customField of applicationFormSettings.customFields ?? []) {
      const customFieldValidation = isApplicationFormCustomFieldValid(customField, event, deepCheck);
      if (!customFieldValidation.isValid) {
        if (!deepCheck) {
          return { isValid: false, errors: [] };
        } else {
          result.isValid = false;
          result.errors?.push({
            field: 'customFields',
            errorList: [`Field has an invalid value`],
          });
          break;
        }
      }
    }
    const applicationFormSettingsValidation: ObjectValidation<ApplicationFormSettings> = validateObject<ApplicationFormSettings>(applicationFormSettings, requiredFields, allowedValuesMap, deepCheck);
    result.errors = [...result.errors, ...(applicationFormSettingsValidation.errors || [])];
    result.isValid = result.isValid && applicationFormSettingsValidation.isValid;
    return result;
  }
  return {isValid: false, errors: []}

}

export function isApplicationFormCustomFieldValid(customField: FieldConfig, event: EventItem | undefined, deepCheck?: boolean)
    : ObjectValidation<FieldConfig> {

    const validationResult = new ObjectValidation<FieldConfig>();
    validationResult.isValid = true;
    let allowedValuesMap: AllowedFieldValueMap<FieldConfig>[] = [];
    let requiredFields: (keyof FieldConfig)[] = []
    let langList = event?.extraLanguages ? [...event.extraLanguages] : [];
    if (event?.defaultLanguage) {
     langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<FieldConfig>(langList, "name", "nameEng", "nameIta", "nameSlo", "nameSr").forEach((k: (keyof FieldConfig)) => {
      requiredFields.push(k);
    });

    customField.name = customField.name?.length === 0 ? undefined : customField.name;
    customField.nameEng = customField.nameEng?.length === 0 ? undefined : customField.nameEng;
    customField.nameIta = customField.nameIta?.length === 0 ? undefined : customField.nameIta;
    customField.nameSlo = customField.nameSlo?.length === 0 ? undefined : customField.nameSlo;
    customField.nameSr = customField.nameSr?.length === 0 ? undefined : customField.nameSr;
    customField.nameBs = customField.nameBs?.length === 0 ? undefined : customField.nameBs;


    switch (customField.fieldConfigTypeId) {
      case FieldConfigType.MULTISELECT:
      case FieldConfigType.SELECT:
        validationResult.isValid = isApplicationFormCustomFieldOptionsValid(customField.options ?? [], langList).isValid;
        requiredFields.push("options");
        break;
      case FieldConfigType.NUMBER:
        requiredFields.push("maxValue");
        requiredFields.push("minValue");
        break;
      case FieldConfigType.TEXT:
        requiredFields.push("maxTextLength");
        requiredFields.push("minTextLength");
        break;
      default:
        break;
    }

    return validationResult.isValid ? validateObject<FieldConfig>(customField, requiredFields, allowedValuesMap, deepCheck) : validationResult;
}


export function isApplicationFormCustomFieldOptionsValid(options: FieldConfigOption[], langList: number[])
    : ObjectValidation<FieldConfigOption> {
  let requiredFields: (keyof FieldConfigOption)[] = []

  getMultiLanguageRequiredFields<FieldConfigOption>(langList, "name", "nameEng", "nameIta", "nameSlo", "nameSr").forEach((k: (keyof FieldConfigOption)) => {
    requiredFields.push(k);
  });

  let validationResult = new ObjectValidation<FieldConfigOption>();
  validationResult.isValid = true;

  if (options.length ?? 0 > 0 ) {
    validationResult.isValid = false;
  }

  for (const option of options) {
    option.name = option.name?.length === 0 ? undefined : option.name;
    option.nameEng = option.nameEng?.length === 0 ? undefined : option.nameEng;
    option.nameIta = option.nameIta?.length === 0 ? undefined : option.nameIta;
    option.nameSlo = option.nameSlo?.length === 0 ? undefined : option.nameSlo;
    option.nameSr = option.nameSr?.length === 0 ? undefined : option.nameSr;
    option.nameBs = option.nameBs?.length === 0 ? undefined : option.nameBs;
    validationResult = validateObject(option, requiredFields);
    if (!validationResult.isValid) {
      return validationResult;
    }
    }
  return validationResult;

}

